import { useTranslation } from 'react-i18next';
import { tradeLabel } from '@utils/tradeLabel';
import type { IFinanceOverview } from '@interfaces/IFinance';
import { fmtUsdMillions, fmtUsdThousands, fmtPct1, fmtSignedPct } from '../format';
import { KpiCard } from './KpiCard';
import styles from './OverviewTab.module.css';

interface IOverviewTabProps {
  overview: IFinanceOverview;
}

export function OverviewTab({ overview }: IOverviewTabProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.kpiRow}>
        <KpiCard
          icon={{ glyph: '$', tone: 'navy' }}
          value={fmtUsdMillions(overview.kpis.totalProjectBudget, 1)}
          label={t('finance.overview.totalProjectBudget')}
          sub={overview.kpis.totalProjectBudgetLabel}
        />
        <KpiCard
          icon={{ glyph: '↗', tone: 'blue' }}
          value={fmtUsdMillions(overview.kpis.spentToDate)}
          label={t('finance.overview.spentToDate')}
          sub={t('finance.overview.spentUtilized', { pct: overview.kpis.spentToDateUtilisedPct })}
        />
        <KpiCard
          icon={{ glyph: '↘', tone: 'amber' }}
          value={`+$${Math.round(overview.kpis.forecastedOverrun / 1000).toLocaleString('en-US')}K`}
          label={t('finance.overview.forecastedOverrun')}
          sub={t('finance.overview.forecastedVariance', { pct: overview.kpis.forecastedOverrunVariancePct })}
        />
        <KpiCard
          icon={{ glyph: '⚠', tone: 'red' }}
          value={String(overview.kpis.costAlerts)}
          label={t('finance.overview.costAlerts')}
          sub={t('finance.overview.costAlertsCritical', { count: overview.kpis.costAlertsCritical })}
        />
      </div>

      <div className={styles.row}>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t('finance.overview.budgetVsActualByTrade')}</h2>
          <div className={styles.tradeList}>
            {overview.budgetVsActual.map((row) => {
              const pct = row.budget > 0 ? (row.spent / row.budget) * 100 : 0;
              const fillPct = Math.min(100, pct);
              return (
                <div key={row.trade} className={styles.tradeRow}>
                  <div className={styles.tradeHead}>
                    <span className={styles.tradeName}>{tradeLabel(t, row.trade)}</span>
                    <span className={styles.tradeValue}>
                      {fmtUsdThousands(row.spent)} / {fmtUsdThousands(row.budget)} ({fmtPct1(pct)})
                    </span>
                  </div>
                  <div className={styles.tradeTrack}>
                    <div
                      className={`${styles.tradeFill} ${styles[`tone_${row.tone}`]}`}
                      style={{ width: `${fillPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t('finance.overview.costHeatmapByFloor')}</h2>
          <div className={styles.heatmap}>
            {overview.costHeatmap.map((row) => (
              <div key={row.floor} className={styles.heatRow}>
                <span className={styles.heatFloor}>{row.floor}</span>
                <div className={styles.heatBar}>
                  <div
                    className={`${styles.heatFill} ${
                      row.tone === 'red' ? styles.heatRed : styles.heatGreen
                    }`}
                    style={{ width: `${heatBarWidth(row.utilisationPct, row.overrunDeltaPct)}%` }}
                  >
                    <span className={styles.heatAmount}>{fmtUsdThousands(row.amount)}</span>
                  </div>
                </div>
                <span
                  className={`${styles.heatPct} ${
                    row.overrunDeltaPct != null ? styles.heatPctRed : styles.heatPctMuted
                  }`}
                >
                  {row.overrunDeltaPct != null
                    ? fmtSignedPct(row.overrunDeltaPct)
                    : `${row.utilisationPct}%`}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={styles.insights}>
        <h2 className={styles.panelTitle}>{t('finance.overview.aiInsights')}</h2>
        <div className={styles.insightList}>
          {overview.aiInsights.map((insight) => (
            <div key={insight.id} className={styles.insightRow}>
              <span
                className={`${styles.insightDot} ${
                  insight.tone === 'over-budget' ? styles.dotRed : styles.dotGreen
                }`}
                aria-hidden="true"
              />
              <span className={styles.insightBody}>{insight.body}</span>
              <span
                className={`${styles.insightPill} ${
                  insight.tone === 'over-budget' ? styles.pillRed : styles.pillGreen
                }`}
              >
                {t(`finance.status.${insight.tone}`)}
              </span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function heatBarWidth(utilisationPct: number, overrunDeltaPct?: number): number {
  if (overrunDeltaPct != null) return 100;
  return Math.max(20, utilisationPct);
}
